import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Bookmark, Download, Gavel, Loader2, Search, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

export default function Judgments() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [favIds, setFavIds] = useState([]);

  // Smart "principles consultant" search
  const [aiQuery, setAiQuery] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  useEffect(() => {
    api.get("/judgments").then((r) => setItems(r.data)).catch(() => {});
    try {
      setFavIds(JSON.parse(localStorage.getItem("aldakheel_fav_principles") || "[]"));
    } catch { /* noop */ }
  }, []);

  const askSamiPrinciple = (r) => {
    const q = (isAr
      ? `بخصوص مبدأ محكمة التمييز: «${r.case_number || ""}»${r.court ? ` (${r.court})` : ""}. اشرح لي هذا المبدأ وما ينطبق منه مع ذكر المواد والقوانين ذات الصلة.`
      : `Regarding this cassation principle: "${r.case_number || ""}". Explain it and what it applies to.`);
    navigate(`/consultant?q=${encodeURIComponent(q)}&ask=1`);
  };

  const runAiSearch = async (e) => {
    if (e) e.preventDefault();
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/judgments")}`);
      return;
    }
    const q = aiQuery.trim();
    if (q.length < 2) return;
    setAiLoading(true);
    try {
      const r = await api.get("/judgments/ai-search", { params: { q } });
      setAiResults(r.data?.results || []);
    } catch {
      setAiResults([]);
    } finally {
      setAiLoading(false);
    }
  };


  const categories = useMemo(
    () => Array.from(new Set(items.map((j) => j.category).filter(Boolean))),
    [items]
  );

  const t = useMemo(() => ({
    eyebrow: isAr ? "مبادئ محكمة التمييز" : "Court of Cassation Principles",
    title: isAr
      ? "مبادئ محكمة التمييز الكويتية."
      : "Principles of the Kuwaiti Court of Cassation.",
    lead: isAr
      ? "مجموعة مبادئ محكمة التمييز مصنّفة حسب الدائرة (جزائي، مدني، إداري، أحوال شخصية، تجاري) لتسهيل البحث والاستشهاد."
      : "Cassation principles grouped by chamber (criminal, civil, administrative, personal-status, commercial) for easy search and citation.",
    search: isAr ? "ابحث برقم القضية أو المحكمة..." : "Search by case number or court…",
    empty: isAr ? "لم تُنشر مبادئ بعد." : "No principles have been published yet.",
    all: isAr ? "الكل" : "All",
    download: isAr ? "تحميل" : "Download",
    read: isAr ? "قراءة المبدأ" : "Read",
    case: isAr ? "رقم القضية" : "Case",
    court: isAr ? "المحكمة" : "Court",
    issued: isAr ? "تاريخ الصدور" : "Issued",
    aiTitle: isAr ? "المستشار الذكي للمبادئ" : "Smart principles consultant",
    aiLead: isAr
      ? "اكتب فكرتك أو سؤالك بالعربي أو باللهجة الكويتية، والنظام يفهم المقصود ويجيبك بأقرب المبادئ المنطبقة."
      : "Describe your question in plain Arabic or Kuwaiti dialect — we find the most relevant principles.",
    aiPlaceholder: isAr
      ? "مثال: يجوز الطعن بالتزوير بعد صدور الحكم؟"
      : "e.g. Can forgery be challenged after a ruling?",
    aiBtn: isAr ? "ابحث بذكاء" : "Smart search",
    aiThinking: isAr ? "جارِ التحليل..." : "Analyzing…",
    aiNone: isAr ? "لم نعثر على مبدأ مطابق. جرّب صياغة أخرى." : "No matching principle found.",
    aiReason: isAr ? "لماذا يناسب:" : "Why it fits:",
    aiResultsTitle: isAr ? "المبادئ الأقرب لموضوعك" : "Closest principles",
  }), [isAr]);

  const filtered = items.filter((j) => {
    if (catFilter && j.category !== catFilter) return false;
    if (favOnly && !favIds.includes(j.id)) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (j.case_number || "").toLowerCase().includes(q) ||
      (j.court || "").toLowerCase().includes(q) ||
      (j.title || "").toLowerCase().includes(q) ||
      (j.summary || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="judgments-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* SMART PRINCIPLES CONSULTANT */}
      <div
        data-testid="principles-ai-search"
        className="mb-12 rounded-sm border p-6 lg:p-8"
        style={{ background: "var(--navy)", borderColor: "var(--navy)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5" style={{ color: "var(--gold)" }} />
          <h2 className="font-display text-xl text-white">{t.aiTitle}</h2>
        </div>
        <p className="text-sm text-white/70 leading-relaxed mb-5 max-w-2xl">{t.aiLead}</p>
        <form onSubmit={runAiSearch} className="flex flex-col sm:flex-row gap-3">
          <Input
            dir="auto"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            placeholder={t.aiPlaceholder}
            data-testid="principles-ai-input"
            className="rounded-sm h-12 bg-white/95 border-0 flex-1 text-base"
          />
          <button
            type="submit"
            disabled={aiLoading || aiQuery.trim().length < 2}
            data-testid="principles-ai-submit"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 text-sm rounded-sm transition-colors disabled:opacity-60"
            style={{ background: "var(--gold-deep)", color: "#fff" }}
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.aiThinking}
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                {t.aiBtn}
              </>
            )}
          </button>
        </form>

        {aiResults !== null && !aiLoading && (
          <div className="mt-6" data-testid="principles-ai-results">
            {aiResults.length === 0 ? (
              <p className="text-sm text-white/70">{t.aiNone}</p>
            ) : (
              <>
                <p className="eyebrow text-white/50 mb-3">{t.aiResultsTitle}</p>
                <ul className="space-y-3">
                  {aiResults.map((r) => (
                    <li key={r.id} data-testid={`principles-ai-result-${r.id}`}>
                      <div className="rounded-sm bg-white/5 border border-white/10 p-4">
                        <div className="flex items-start gap-3">
                          <Gavel className="w-4 h-4 shrink-0 mt-1" style={{ color: "var(--gold)" }} />
                          <div className="min-w-0" dir="rtl">
                            <Link
                              to={`/judgments/${r.id}${
                                r.matched?.length
                                  ? `?q=${encodeURIComponent(r.matched[0])}`
                                  : ""
                              }`}
                              className="font-display text-base text-white leading-snug hover:text-[var(--gold)] transition-colors block"
                            >
                              {r.case_number}
                            </Link>
                            {r.court && (
                              <p className="text-xs text-white/50 mt-0.5">{r.court}</p>
                            )}
                            {r.reason && (
                              <p className="text-sm text-[var(--gold)] mt-2 leading-relaxed">
                                <span className="text-white/50">{t.aiReason} </span>
                                {r.reason}
                              </p>
                            )}
                            {r.excerpt && (
                              <p className="text-sm text-white/75 mt-2 leading-relaxed line-clamp-3">
                                {r.excerpt}
                              </p>
                            )}
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                              <Link
                                to={`/judgments/${r.id}${
                                  r.matched?.length ? `?q=${encodeURIComponent(r.matched[0])}` : ""
                                }`}
                                data-testid={`principles-ai-read-${r.id}`}
                                className="inline-flex items-center gap-1.5 px-3 h-8 text-[11px] uppercase tracking-[0.14em] rounded-sm bg-white/10 hover:bg-white/20 text-white transition-colors"
                              >
                                <BookOpen className="w-3.5 h-3.5" /> {t.read}
                              </Link>
                              <button
                                type="button"
                                onClick={() => askSamiPrinciple(r)}
                                data-testid={`principles-ai-asksami-${r.id}`}
                                className="inline-flex items-center gap-1.5 px-3 h-8 text-[11px] uppercase tracking-[0.14em] rounded-sm transition-colors"
                                style={{ background: "var(--gold-deep)", color: "#fff" }}
                              >
                                <Sparkles className="w-3.5 h-3.5" /> {isAr ? "اسأل سامي" : "Ask Sami"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mb-8 max-w-md relative">
        <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          dir="auto"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          data-testid="judgments-search"
          className="rounded-sm h-11 ps-9"
        />
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={() => setFavOnly((v) => !v)}
          data-testid="principles-fav-toggle"
          className={`inline-flex items-center gap-2 px-4 h-9 text-xs rounded-full border transition-colors ${
            favOnly ? "text-white" : "hover:bg-[var(--beige)]"
          }`}
          style={{
            borderColor: "var(--gold-deep)",
            background: favOnly ? "var(--gold-deep)" : "transparent",
            color: favOnly ? "#fff" : "var(--ink)",
          }}
        >
          <Bookmark className="w-3.5 h-3.5" />
          {isAr ? `المفضلة (${favIds.length})` : `Saved (${favIds.length})`}
        </button>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="principles-cat-filters">
          <button
            type="button"
            onClick={() => setCatFilter("")}
            data-testid="principles-cat-all"
            className={`px-4 h-9 text-xs rounded-full border transition-colors ${
              catFilter === "" ? "text-white" : "hover:bg-[var(--beige)]"
            }`}
            style={{
              borderColor: "var(--gold-deep)",
              background: catFilter === "" ? "var(--gold-deep)" : "transparent",
              color: catFilter === "" ? "#fff" : "var(--ink)",
            }}
          >
            {t.all} ({items.length})
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCatFilter(c)}
              data-testid={`principles-cat-${c}`}
              className={`px-4 h-9 text-xs rounded-full border transition-colors ${
                catFilter === c ? "text-white" : "hover:bg-[var(--beige)]"
              }`}
              style={{
                borderColor: "var(--gold-deep)",
                background: catFilter === c ? "var(--gold-deep)" : "transparent",
                color: catFilter === c ? "#fff" : "var(--ink)",
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">
          {t.empty}
        </div>
      ) : (
        <ul className="border border-slate-200 divide-y divide-slate-200 bg-white">
          {filtered.map((j) => (
            <li
              key={j.id}
              data-testid={`judgment-item-${j.id}`}
              className="grid lg:grid-cols-12 gap-6 p-6 lg:p-8 hover:bg-slate-50 transition-colors"
            >
              <div className="lg:col-span-2 flex items-start gap-3">
                <Gavel className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                <div>
                  <p className="eyebrow">{t.case}</p>
                  <p className="font-mono text-sm mt-1">{j.case_number}</p>
                </div>
              </div>
              <div className="lg:col-span-3">
                <p className="eyebrow">{t.court}</p>
                <p className="text-sm mt-1">{j.court || "—"}</p>
                {j.category && (
                  <span
                    className="inline-block mt-2 px-2.5 py-0.5 text-[11px] rounded-full"
                    style={{ background: "rgba(201,169,97,0.15)", color: "var(--gold-deep)" }}
                  >
                    {j.category}
                  </span>
                )}
                {j.date_issued && (
                  <p className="text-xs text-slate-500 mt-2">
                    {t.issued}: {j.date_issued}
                  </p>
                )}
              </div>
              <div className="lg:col-span-5">
                {j.title && (
                  <p className="font-display text-base mb-1 leading-snug" dir="rtl">
                    {j.title}
                  </p>
                )}
                {j.summary && (
                  <p className="text-sm text-slate-700 leading-relaxed" dir="rtl">
                    {j.summary}
                  </p>
                )}
              </div>
              <div className="lg:col-span-2 flex lg:flex-col lg:items-end gap-2">
                <Link
                  to={`/judgments/${j.id}`}
                  data-testid={`judgment-read-${j.id}`}
                  className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] text-white transition-colors w-full lg:w-auto justify-center"
                  style={{ background: "var(--navy)" }}
                >
                  <BookOpen className="w-3.5 h-3.5" /> {t.read}
                </Link>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/judgments/${j.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`judgment-download-${j.id}`}
                  className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-slate-900 transition-colors w-full lg:w-auto justify-center"
                >
                  <Download className="w-3.5 h-3.5" /> {t.download}
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
